import {ChatMessage, DataObjectId, Hub, SandboxTree} from 'yougile-model'
import {Util, FilenameUtil, logger} from 'yougile-util'
import {Sync} from '../../client/sync'
import {ChatUtils} from '../../extensions/chat-utils'
import {Item} from './item'
import {Items} from './items'
import {UIElement} from './ui-element'
import {UIRegistry} from './ui-registry'
import {UserObj} from './user-obj'

/**
 * @name=message variable
 * @code
 * Chat.listMessages(Items.get('... task id'));
 * @description
 * Representation of a message in a tasks’s chat
 * (see Chat.listMessages).
 * @descriptionRu
 * Переменная представляеющая сообщение в чате по задаче
 * (см. Chat.listMessages).
 */
export class MessageObj {
  public box: SandboxTree /* api:exclude */
  private id: DataObjectId

  constructor(box: SandboxTree, taskId: DataObjectId, timestamp: number) {
    this.box = box
    this.id = ChatUtils.calculateMessageId(taskId, timestamp)
  }

  /**
   * @code
   * (...).timestamp;
   * @description
   * Unit timestamp of message creation.
   * @descriptionRu
   * Время отправки сообщения (unix timestamp).
   */
  public get timestamp(): number {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    return msg.timestamp
  }

  /**
   * @code
   * (...).from;
   * @description
   * Sender user.
   * @descriptionRu
   * Отправитель сообщения.
   */
  public get from(): UserObj {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    return new UserObj(msg.from, this.box)
  }

  public set from(val: UserObj) {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    const t = ChatUtils.getTaskFromId(this.id, this.box)
    msg.from = val.id
    t.commit()
  }

  /**
   * @code
   * (...).text;
   * @description
   * Message text.
   * @descriptionRu
   * Текст сообщения.
   */
  public get text(): string {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    return msg.text.replace(FilenameUtil.rx, '').trim()
  }

  public set text(val: string) {
    if (typeof val === 'undefined') {
      logger.error('Text must be defined')
      return
    }
    const msg = ChatUtils.getMessageById(this.id, this.box)
    const files: string[] = []
    const oldText = (msg.text || '').replace(FilenameUtil.rx, part => {
      files.push('/root/#file:' + part)
      return ''
    })
    if (oldText === val) {
      return
    } else {
      const pf = files && files.length ? '\n' + files.join(' ') : ''
      const t = ChatUtils.getTaskFromId(this.id, this.box)
      msg.text = val.trim() + pf
      t.commit()
    }
  }

  /**
   * @code
   * (...).deleted;
   * @description
   * True if message is deleted.
   * @descriptionRu
   * Удалено ли сообщение.
   */
  public get deleted(): boolean {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    return msg.deleted
  }

  public set deleted(val: boolean) {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    const t = ChatUtils.getTaskFromId(this.id, this.box)
    msg.deleted = val
    t.commit()
  }

  /**
   * @code
   * (...).ui;
   * @example
   * var task = Items.get('... some task id');
   * // getting a first message
   * var m = Chat.listMessages(task)[0];
   * m.ui.style = {
   *   background: '#ff0'
   * };
   */
  public get ui(): UIElement {
    const uiReg = UIRegistry.get(this.box.companyId)
    const old = uiReg.uiRegistry.get(this.id)
    if (old) {
      return old
    } else {
      const el = new UIElement(this.box.companyId, this.id, undefined)
      uiReg.uiRegistry.set(this.id, el)
      return el
    }
  }

  /**
   * @code
   * (...).getTask();
   * @description
   * Gets the corresponding task.
   * @descriptionRu
   * Возвращает соответствующую задачу.
   */
  public getTask(): Item {
    return Items.fromHub(ChatUtils.getTaskFromId(this.id, this.box))
  }

  /**
   * @code
   * (...).getData();
   * @description
   * Gets custom data attached to the message (see setData function).
   * @descriptionRu
   * Получает пользовательские данные, прикрепленные к сообщению.
   * (см. функцию setData).
   */
  public getData(): unknown {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    return msg.apiData ? Util.clone(msg.apiData) : {}
  }

  /**
   * @code
   * (...).setData(...);
   * @description
   * Custom data can be attached to any message
   * (JSON). This data is automatically
   * synchronized with server and all users and if available
   * with getData function.
   * @descriptionRu
   * К любому объекту YouGile можно добавить пользовательские
   * данные (JSON). Они будут автоматически синхронизированы
   * с сервером и всеми юзерами и доступны через
   * функцию getData.
   */
  public setData(val: unknown): void {
    const msg = ChatUtils.getMessageById(this.id, this.box)
    const t = ChatUtils.getTaskFromId(this.id, this.box)
    msg.apiData = val
    t.commit()
  }

  public static fromMessageAndHub(msg: ChatMessage, hub: Hub): MessageObj {
    return new MessageObj(Sync.get(hub), hub.id, msg.timestamp)
  }
}
